// ===== Packages =====

import React, {
    useRef,
    useState,
    useCallback,
    useEffect,
}                                   from 'react';
import ReactDOM                     from 'react-dom';

// ===== Components =====

import { Transition }               from '../Transition';

// ===== Services =====

import {
    playAudio,
    getPressEventHandlers,
}                                   from '../../services';
import useLazyAudioRef               from '../../services/useLazyAudioRef';

// ===== Hooks =====

import { useTimeout }               from '../../hooks';

// ===== Assets =====

import ModalEnter                   from '../../sounds/swoosh_in.mp3';
import ModalExit                    from '../../sounds/swoosh_out.mp3';

// ===== Constants =====

import {
    DEFAULT_AUDIO_VOLUME,
    MODAL_Z_INDEX,
}                                   from '../../constants/generalConstants';

// ===== Styles =====

import {
    ModalContainer,
    ModallessContainer,
}                                   from './styles';
import {
    Overlay,
}                                   from '../../styles';
import { theme }                    from '../../themes/theme-context';

const root = window.document.getElementById('root');

interface Props {
    customCursorActive: boolean,
    isOpen: boolean,
    color?: string,
    width?: number,
    hasSound: boolean,
    hasContainer: boolean,
    annotationEditorOwned?: boolean,
    onCloseInteractionStart?: () => void,
    overrideClickOverlay?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement,
    closeModal?: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function Modal({
    customCursorActive,
    isOpen,
    color,
    width,
    hasSound,
    children,
    annotationEditorOwned = false,
    onCloseInteractionStart,
    overrideClickOverlay,
    closeModal,
    hasContainer,
}: Props): JSX.Element {
    // ===== Refs =====

    // ----- Sound Clips

    const modalEnterClip = useLazyAudioRef({
        src: ModalEnter,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const modalExitClip = useLazyAudioRef({
        src: ModalExit,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const previousIsOpenRef = useRef<boolean>(false);

    // ===== State ====

    const [isVisible, setIsVisible] = useState(false);

    // ===== Animation Constants =====

    // Duration is takes to enter and exit Modal
    const MODAL_CONTENTS_TRANSITION_DURATION = 150;

    // Duration it takes for modal to enter viewport
    const MODAL_ENTER_DURATION = 150;

    // Duration it takes for modal to exit viewport
    const MODAL_EXIT_DURATION = 150;

    // ===== Methods =====

    const handleClick = (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent): void => {
        if (overrideClickOverlay) return;
        // Some parent editors dismiss on the next pointer/click event after a
        // portal modal closes, so let them suppress that short follow-up burst.
        if (onCloseInteractionStart) onCloseInteractionStart();
        e?.preventDefault();
        e?.stopPropagation();
        setIsVisible(false);
        delayBroadcastModalExit(e);

        // Play Sound
        if (hasSound) {
            modalExitClip.current.pause();
            modalExitClip.current.currentTime = 0;
            playAudio(modalExitClip.current);
        }
    };

    // ===== Side Effects =====

    useEffect(() => {
        /**
         * Modal enter/exit work should run only when the open state actually
         * toggles. Parent rerenders while a modal stays open must not replay
         * the enter sound or restart the enter timeout.
         */
        if (isOpen === previousIsOpenRef.current) {
            return;
        }

        previousIsOpenRef.current = isOpen;

        if (isOpen) {
            delayModalEnter();
        } else if (isVisible) {
            setIsVisible(false);

            // Play Sound
            if (hasSound) {
                modalExitClip.current.pause();
                modalExitClip.current.currentTime = 0;
                playAudio(modalExitClip.current);
            }
        }
    }, [hasSound, isOpen, isVisible]);

    // Determine initial isVisible State
    const {
        start: delayModalEnter,
    } = useTimeout(() => {
        setIsVisible(true);

        // Play Sound
        if (hasSound) {
            modalEnterClip.current.pause();
            modalEnterClip.current.currentTime = 0;
            playAudio(modalEnterClip.current);
        }
    }, MODAL_ENTER_DURATION);

    const {
        start: delayBroadcastModalExit,
    } = useTimeout((e) => {
        if (closeModal) closeModal(e as React.MouseEvent | React.TouchEvent | React.SyntheticEvent);
    }, MODAL_EXIT_DURATION);

    // ===== Rendering =====

    const renderOverlay = useCallback(() => {
        const defaultStyle = {
            opacity: 0,
            zIndex: -1,
            transition: 'opacity 200ms ease-in',
        };

        interface OverlayTransitionStyle {
            entering: {
                opacity: number,
                zIndex: number,
            },
            entered: {
                opacity: number,
                zIndex: number,
            },
            exiting: {
                transitionDelay: string,
                zIndex: number,
            },
            [index: string]: {
                opacity: number,
                zIndex: number,
            } | {
                transitionDelay: string,
                zIndex: number,
            }
        }
        const transitionStyles: OverlayTransitionStyle = {
            entering: {
                opacity: 0,
                zIndex: -1,
            },
            entered: {
                opacity: 1,
                zIndex: MODAL_Z_INDEX,
            },
            exiting: {
                transitionDelay: '300ms',
                zIndex: MODAL_Z_INDEX,
            },
        };

        return (
            <Transition
                in={isOpen}
                timeout={{
                    enter: MODAL_ENTER_DURATION,
                    exit: MODAL_EXIT_DURATION,
                }}
                appear
                mountOnEnter
            >
                {(state) => (
                    <Overlay
                        customCursorActive={customCursorActive}
                        data-annotation-editor-modal={annotationEditorOwned ? 'true' : 'false'}
                        style={{
                            ...defaultStyle,
                            ...transitionStyles[state],
                        }}
                        {...getPressEventHandlers({ onMouseDown: handleClick })}
                    />
                )}
            </Transition>
        );
    }, [isOpen]);

    const renderModalContents = useCallback(() => {
        const defaultStyle = {
            opacity: 0,
            top: -1000,
            transform: 'translate(-50%, -50%)',
            transition: `top 300ms ${theme.motion.overshoot}, opacity 100ms ease-in`,
        };

        interface ModalContentsTransitionStyles {
            entering: {
                top: number,
                opacity: number,
                transform: string,
            },
            entered: {
                opacity: number,
                top: string,
                transform: string,
            },
            exiting: {
                opacity: number,
                top: string,
                transition: string,
            },
            exited: {
                top: number,
                opacity: number,
            },
            [index: string]: {
                top: number,
                opacity: number,
                transform: string,
            } | {
                top: number,
                opacity: number,
            } | {
                opacity: number,
                top: string,
                transition: string,
            } | {
                opacity: number,
                top: string,
                transform: string,
            }
        }
        const transitionStyles: ModalContentsTransitionStyles = {
            entering: {
                top: -1000,
                opacity: 0,
                transform: 'translate(-50%, -50%)',
            },
            entered: {
                opacity: 1,
                top: '50%',
                transform: 'translate(-50%, -50%)',
            },
            exiting: {
                opacity: 1,
                top: '50%',
                transition: 'top 300ms ease-out, opacity 300ms ease-out',
            },
            exited: {
                top: 0,
                opacity: 0,
            },
        };

        if (hasContainer) {
            return (
                <Transition
                    in={isVisible}
                    timeout={{
                        enter: MODAL_CONTENTS_TRANSITION_DURATION,
                        exit: MODAL_CONTENTS_TRANSITION_DURATION,
                    }}
                    appear
                    mountOnEnter
                >
                    {(state) => (
                        <ModalContainer
                            id="modal"
                            customCursorActive={customCursorActive}
                            color={color}
                            width={width}
                            isVisible={isVisible}
                            data-annotation-editor-modal={annotationEditorOwned ? 'true' : 'false'}
                            style={{
                                ...defaultStyle,
                                ...transitionStyles[state],
                            }}
                        >
                            {React.cloneElement(children, { handleClose: handleClick })}
                        </ModalContainer>
                    )}
                </Transition>
            );
        }

        return (
            <Transition
                in={isVisible}
                timeout={{
                    enter: MODAL_CONTENTS_TRANSITION_DURATION,
                    exit: MODAL_CONTENTS_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
            >
                {(state) => (
                    <ModallessContainer
                        isVisible={isVisible}
                        data-annotation-editor-modal={annotationEditorOwned ? 'true' : 'false'}
                        style={{
                            ...defaultStyle,
                            ...transitionStyles[state],
                        }}
                    >
                        {React.cloneElement(children, { handleClose: handleClick })}
                    </ModallessContainer>
                )}
            </Transition>
        );
    }, [
        annotationEditorOwned,
        hasContainer,
        isVisible,
        children,
        color,
        customCursorActive,
        handleClick,
        width,
    ]);

    return (
        ReactDOM.createPortal(
            <div>
                {renderOverlay()}
                {renderModalContents()}
            </div>,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            root!,
        )
    );
}

export default Modal;
